import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2AdminDocuments: build.query<
      GetV2AdminDocumentsApiResponse,
      GetV2AdminDocumentsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/admin/documents`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          document_type: queryArg.documentType,
          organization_id: queryArg.organizationId,
          patient_id: queryArg.patientId,
          include_deleted: queryArg.includeDeleted,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          search: queryArg.search,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
        },
      }),
    }),
    getV2AdminDocumentsByDocumentId: build.query<
      GetV2AdminDocumentsByDocumentIdApiResponse,
      GetV2AdminDocumentsByDocumentIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/admin/documents/${queryArg.documentId}`,
      }),
    }),
    getV2AdminOrganizationsByOrganizationIdDocuments: build.query<
      GetV2AdminOrganizationsByOrganizationIdDocumentsApiResponse,
      GetV2AdminOrganizationsByOrganizationIdDocumentsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/admin/organizations/${queryArg.pathOrganizationId}/documents`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          document_type: queryArg.documentType,
          organization_id: queryArg.queryOrganizationId,
          patient_id: queryArg.patientId,
          include_deleted: queryArg.includeDeleted,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          search: queryArg.search,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
        },
      }),
    }),
    getV2ProviderDocuments: build.query<
      GetV2ProviderDocumentsApiResponse,
      GetV2ProviderDocumentsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          document_type: queryArg.documentType,
          organization_id: queryArg.organizationId,
          patient_id: queryArg.patientId,
          include_deleted: queryArg.includeDeleted,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          search: queryArg.search,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
        },
      }),
    }),
    postV2ProviderDocumentsGenerateAndSend: build.mutation<
      PostV2ProviderDocumentsGenerateAndSendApiResponse,
      PostV2ProviderDocumentsGenerateAndSendApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/generate-and-send`,
        method: "POST",
        body: queryArg.generateAndSendRequest,
      }),
    }),
    getV2ProviderDocumentsByDocumentId: build.query<
      GetV2ProviderDocumentsByDocumentIdApiResponse,
      GetV2ProviderDocumentsByDocumentIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/${queryArg.documentId}`,
      }),
    }),
    postV2ProviderEncountersByEncounterIdDocuments: build.mutation<
      PostV2ProviderEncountersByEncounterIdDocumentsApiResponse,
      PostV2ProviderEncountersByEncounterIdDocumentsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/encounters/${queryArg.encounterId}/documents`,
        method: "POST",
        body: queryArg.createDocumentRequest,
      }),
    }),
    deleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentId:
      build.mutation<
        DeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse,
        DeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg
      >({
        query: (queryArg) => ({
          url: `/v2/provider/encounters/${queryArg.encounterId}/documents/${queryArg.documentId}`,
          method: "DELETE",
        }),
      }),
    getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: build.query<
      GetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse,
      GetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/encounters/${queryArg.encounterId}/documents/${queryArg.documentId}`,
      }),
    }),
    patchV2ProviderEncountersByEncounterIdDocumentsAndDocumentId:
      build.mutation<
        PatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse,
        PatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg
      >({
        query: (queryArg) => ({
          url: `/v2/provider/encounters/${queryArg.encounterId}/documents/${queryArg.documentId}`,
          method: "PATCH",
          body: queryArg.documentUpdate,
        }),
      }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2AdminDocumentsApiResponse =
  /** status 200 OK */ DocumentListResponse;
export type GetV2AdminDocumentsApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Filter by document type (e.g., 'lab_order', 'claim', 'form') */
  documentType?: string | null;
  /** Filter by organization ID */
  organizationId?: string | null;
  /** Filter by patient ID */
  patientId?: string | null;
  /** Whether to include soft-deleted documents */
  includeDeleted?: boolean;
  /** Start date for filtering (ISO 8601 format) */
  startDate?: string | null;
  /** End date for filtering (ISO 8601 format) */
  endDate?: string | null;
  /** Text search on filename and document title */
  search?: string | null;
  /** Field to sort by (created_at, updated_at, filename, size_bytes) */
  sortBy?: string;
  /** Sort order (asc or desc) */
  sortOrder?: string;
};
export type GetV2AdminDocumentsByDocumentIdApiResponse =
  /** status 200 OK */ DocumentDetailResponse;
export type GetV2AdminDocumentsByDocumentIdApiArg = {
  documentId: string;
};
export type GetV2AdminOrganizationsByOrganizationIdDocumentsApiResponse =
  /** status 200 OK */ DocumentListResponse;
export type GetV2AdminOrganizationsByOrganizationIdDocumentsApiArg = {
  pathOrganizationId: string;
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Filter by document type (e.g., 'lab_order', 'claim', 'form') */
  documentType?: string | null;
  /** Filter by organization ID */
  queryOrganizationId?: string | null;
  /** Filter by patient ID */
  patientId?: string | null;
  /** Whether to include soft-deleted documents */
  includeDeleted?: boolean;
  /** Start date for filtering (ISO 8601 format) */
  startDate?: string | null;
  /** End date for filtering (ISO 8601 format) */
  endDate?: string | null;
  /** Text search on filename and document title */
  search?: string | null;
  /** Field to sort by (created_at, updated_at, filename, size_bytes) */
  sortBy?: string;
  /** Sort order (asc or desc) */
  sortOrder?: string;
};
export type GetV2ProviderDocumentsApiResponse =
  /** status 200 OK */ DocumentListResponse;
export type GetV2ProviderDocumentsApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Filter by document type (e.g., 'lab_order', 'claim', 'form') */
  documentType?: string | null;
  /** Filter by organization ID */
  organizationId?: string | null;
  /** Filter by patient ID */
  patientId?: string | null;
  /** Whether to include soft-deleted documents */
  includeDeleted?: boolean;
  /** Start date for filtering (ISO 8601 format) */
  startDate?: string | null;
  /** End date for filtering (ISO 8601 format) */
  endDate?: string | null;
  /** Text search on filename and document title */
  search?: string | null;
  /** Field to sort by (created_at, updated_at, filename, size_bytes) */
  sortBy?: string;
  /** Sort order (asc or desc) */
  sortOrder?: string;
};
export type PostV2ProviderDocumentsGenerateAndSendApiResponse =
  /** status 200 OK */ GenerateAndSendResponse;
export type PostV2ProviderDocumentsGenerateAndSendApiArg = {
  generateAndSendRequest: GenerateAndSendRequest;
};
export type GetV2ProviderDocumentsByDocumentIdApiResponse =
  /** status 200 OK */ DocumentDetailResponse;
export type GetV2ProviderDocumentsByDocumentIdApiArg = {
  documentId: string;
};
export type PostV2ProviderEncountersByEncounterIdDocumentsApiResponse =
  /** status 201 Created */ DocumentResponse;
export type PostV2ProviderEncountersByEncounterIdDocumentsApiArg = {
  encounterId: string;
  createDocumentRequest: CreateDocumentRequest;
};
export type DeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse =
  unknown;
export type DeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg =
  {
    encounterId: string;
    documentId: string;
  };
export type GetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse =
  /** status 200 OK */ DocumentResponse;
export type GetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg = {
  encounterId: string;
  documentId: string;
};
export type PatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiResponse =
  /** status 200 OK */ DocumentResponse;
export type PatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdApiArg =
  {
    encounterId: string;
    documentId: string;
    documentUpdate: DocumentUpdate;
  };
export type PatientInfo = {
  dob?: string | null;
  first_name?: string | null;
  identifier?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  name?: string | null;
  patient_id: string;
  preferred_name?: string | null;
  telephone?: string | null;
};
export type DocumentSummaryResponse = {
  /** MIME type (e.g., 'application/pdf') */
  content_type: string;
  /** When document was created (ISO 8601 UTC) */
  created_at: string;
  /** Unique identifier for the document */
  document_id: string;
  /** User-friendly title for the document */
  document_title?: string | null;
  /** Type of document (e.g., 'lab_order', 'claim', 'form') */
  document_type?: string | null;
  /** Original filename */
  filename: string;
  /** Patient information if patient_id is set */
  patient?: PatientInfo | null;
  /** Patient associated with the document */
  patient_id?: string | null;
  /** File size in bytes */
  size_bytes: number;
};
export type DocumentListResponse = {
  items: DocumentSummaryResponse[];
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type PatientInfo2 = {
  dob?: string | null;
  first_name?: string | null;
  identifier?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  name?: string | null;
  patient_id: string;
  preferred_name?: string | null;
  telephone?: string | null;
};
export type DocumentDetailResponse = {
  /** MIME type (e.g., 'application/pdf') */
  content_type: string;
  /** When document was created (ISO 8601 UTC) */
  created_at: string;
  /** When document was deleted (ISO 8601 UTC) */
  deleted_at?: string | null;
  /** Unique identifier for the document */
  document_id: string;
  /** User-friendly title for the document */
  document_title?: string | null;
  /** Type of document (e.g., 'lab_order', 'claim', 'form') */
  document_type?: string | null;
  /** Presigned S3 URL for downloading the document */
  download_url?: string | null;
  /** Unix timestamp when download URL expires */
  download_url_expires_at?: number | null;
  /** Original filename */
  filename: string;
  /** MD5 checksum for integrity */
  md5_hash: string;
  /** Organization the document belongs to */
  organization_id?: string | null;
  /** Patient information if patient_id is set */
  patient?: PatientInfo2 | null;
  /** Patient associated with the document */
  patient_id?: string | null;
  /** Provider who owns the document */
  provider_id: string;
  /** File size in bytes */
  size_bytes: number;
  /** When document was last updated (ISO 8601 UTC) */
  updated_at?: string | null;
};
export type GenerateAndSendResponse = {
  /** Channels the document was sent through */
  channels: string[];
  /** ID of the generated document */
  document_id: string;
  /** Success or error message */
  message: string;
  /** Patient the document was sent to */
  patient_id: string;
  /** Whether the operation was successful */
  success: boolean;
};
export type GenerateAndSendRequest = {
  /** Communication channels (e.g., ['sms'], ['email'], or both) */
  channels: string[];
  /** Optional title for the document */
  document_title?: string | null;
  /** Markdown content to convert to PDF */
  markdown: string;
  /** Optional custom message to include with the document */
  message?: string | null;
  /** Patient to send the document to */
  patient_id: string;
};
export type DocumentResponse = {
  /** Document content */
  content?: string | null;
  /** Creation timestamp */
  created_at: string;
  /** Document description */
  description?: string | null;
  /** Document ID */
  encounter_document_id: string;
  /** Last modification timestamp */
  modified_at: string;
  /** Document title */
  title?: string | null;
  /** Transcription session ID for recording documents */
  transcription_session_id?: string | null;
  /** Document type */
  type: "text" | "recording" | "document" | "note";
};
export type CreateDocumentRequest = {
  /** Document content */
  content?: string | null;
  /** Document description */
  description?: string | null;
  /** Document title */
  title?: string | null;
  /** Transcription session ID for recording documents */
  transcription_session_id?: string | null;
  /** Document type */
  type: "text" | "recording" | "document" | "note";
};
export type DocumentUpdate = {
  /** Document content */
  content?: string | null;
  /** Document description */
  description?: string | null;
  /** Document title */
  title?: string | null;
};
